import { StrictMode, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { TooltipProvider } from '@/components/ui/tooltip';
import { NotFound } from '@/components/errors/NotFound';
import { AppErrorFallback } from '@/components/errors/AppErrorFallback';
import { initSentry, SentryErrorBoundary } from '@/config/sentry';
import { loadFirebaseConfig } from '@/lib/firebase';
import { routeTree } from './routeTree.gen';
import './index.css';

// Before the app renders, so a crash during the first paint is still reported.
initSentry();

const router = createRouter({ routeTree, defaultNotFoundComponent: NotFound });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Serving config is cached for 60 s server-side anyway (§4.2), so a
      // shorter client cache would only add requests without adding freshness.
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Auth and validation failures will not fix themselves on a retry.
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const rootElement = document.getElementById('root');
if (rootElement && !rootElement.innerHTML) {
  // Resolve the Firebase web config before the first paint, so `getFirebaseAuth`
  // can stay synchronous everywhere it is called. A failure here renders the
  // error boundary rather than a blank page — a misconfigured console that
  // looks broken is easier to diagnose than one that looks empty.
  const bootstrap = loadFirebaseConfig();
  const root = ReactDOM.createRoot(rootElement);

  const render = (children: ReactNode) =>
    root.render(
      <StrictMode>
        <SentryErrorBoundary
          fallback={({ resetError }) => <AppErrorFallback resetError={resetError} />}
        >
          {children}
        </SentryErrorBoundary>
      </StrictMode>
    );

  bootstrap.then(
    () =>
      render(
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AuthProvider>
              <TooltipProvider>
                <RouterProvider router={router} />
              </TooltipProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      ),
    () => render(<AppErrorFallback resetError={() => window.location.reload()} />)
  );
}
